'use client';

import Image from '@repo/ui/src/components/Image';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import BuyOnlineButton from '@repo/ui/src/components/BuyOnlineButton';
import ProductBanner from '@repo/ui/src/components/ContentGrid/ProductBanner';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import RatingStars from '@repo/ui/src/components/RatingStars';
import LocallyDropdown, { type LocallyConfig } from '@repo/ui/src/components/LocallyDropdown';
import { Drawer } from '@repo/ui/src/modules/UI/components/Drawer';
import { ModalID, useModal, type ProductDetailsData } from '@repo/ui/src/modules/UI';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import ExpandableText from '@repo/ui/src/components/ExpandableText';
import ProductPrice from '@repo/ui/src/components/Product/Price';
import React, { useState, useEffect } from 'react';

const MIN_HEIGHT_FOR_LOCALLY_DROPDOWN = 824;

interface ProductDetailsDrawerProps {
  locallyConfig?: LocallyConfig;
  vip?: boolean;
  renderAboveLocally?: (product: ProductDetailsData) => React.ReactNode;
  renderCTA?: (product: ProductDetailsData) => React.ReactNode;
}

const ProductDetailsDrawer = ({
  locallyConfig,
  vip,
  renderAboveLocally,
  renderCTA,
}: ProductDetailsDrawerProps) => {
  const { data: product, close } = useModal(ModalID.productDetails);
  const { t } = useTranslation();
  const [windowHeight, setWindowHeight] = useState(MIN_HEIGHT_FOR_LOCALLY_DROPDOWN);

  useEffect(() => {
    setWindowHeight(window.innerHeight);
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showLocallyDropdown = windowHeight >= MIN_HEIGHT_FOR_LOCALLY_DROPDOWN;

  const href = product ? product.link || product.deliveryKey || product.productUrl || '#' : '#';
  const activeGrillSizeValue = product?.variationValues?.find(
    (v) => v.name === 'grill_size',
  )?.value;
  const grillSize = activeGrillSizeValue
    ? product?.variations?.find((v) => v.attributesMap?.grill_size === activeGrillSizeValue)
        ?.grillSize
    : undefined;
  const productTitle = product ? product.shortTitle || product.title || '' : '';
  const imageUrl = product
    ? (product.mainImageUrl ?? buildImageSrc(product.images?.[0] ?? null, 300, 1, 1))
    : null;

  return (
    <Drawer id={ModalID.productDetails} className="sm:max-w-xl !bg-warm-100">
      <div className="flex justify-end">
        <button
          onClick={close}
          aria-label={t('productCardContent.close', undefined, { default: 'Close' })}
          className="p-6 bg-warm-200 tra-btn-bare"
        >
          <CloseIcon />
        </button>
      </div>

      {product && (
        <div className="px-6 pb-8 flex flex-col gap-4">
          <ProductBanner product={product} vip={vip} className="w-fit" />

          <div className="flex gap-4 items-center">
            {imageUrl && (
              <div className="w-[120px] shrink-0">
                <Image
                  src={imageUrl}
                  alt={productTitle}
                  layout="responsive"
                  objectFit="contain"
                  width={300}
                  height={300}
                />
              </div>
            )}
            <div className="space-y-2">
              <h2 className="tra-text-web-heading-6 mb-0 py-0">{productTitle}</h2>
              {grillSize && (
                <p className="tra-text-web-body-m text-neutral-500">
                  {`${grillSize} ${t('productDetailPageContent.cookingAreaText', undefined, { default: 'cooking area' })}`}
                </p>
              )}
              <ProductPrice
                product={product}
                variant="h7"
                hideDiscount
                className="mb-0 shrink-0"
                vip={vip}
              />
            </div>
          </div>

          {(product.ratingSummary?.average != null || product.sku) && (
            <div className="flex items-center gap-3">
              {product.ratingSummary?.average != null && (
                <RatingStars
                  value={product.ratingSummary.average}
                  reviewCount={product.ratingSummary.count}
                  size="small"
                  hideValue
                  showCountInParentheses
                />
              )}
              {product.ratingSummary?.average != null && product.sku && (
                <span className="self-stretch border-l border-neutral-300" />
              )}
              {product.sku && (
                <p className="tra-text-web-body-m text-neutral-500 mb-0">{product.sku}</p>
              )}
            </div>
          )}

          {(product.shortDescription || product.description) && (
            <ExpandableText
              text={product.shortDescription || product.description || ''}
              collapsedLines={2}
              showMoreText={t('productCardContent.readMore', undefined, { default: 'Read more' })}
              showLessText={t('productCardContent.readLess', undefined, { default: 'Read less' })}
            />
          )}

          {renderAboveLocally?.(product)}

          {showLocallyDropdown && locallyConfig && product.data?.shopData?.upc && (
            <LocallyDropdown upc={product.data.shopData.upc} config={locallyConfig} nDealers={2} />
          )}

          <div className="flex flex-col gap-3">
            {renderCTA
              ? renderCTA(product)
              : locallyConfig &&
                product.data?.shopData?.upc && (
                  <BuyOnlineButton upc={product.data.shopData.upc} config={locallyConfig} />
                )}
            <Button as={Anchor} link={href} variant={ButtonVariant.secondary} full>
              {t('productCardContent.viewDetails', undefined, { default: 'View Details' })}
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default ProductDetailsDrawer;
